import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Messages } from 'primereact/messages';
import { ProgressSpinner } from '../../../components/PrimereactComponents';
import { getApiClient } from '../../../api';
import type { MunicipalityTranslationsResponse, MunicipalityTranslationsEditRequest } from '../../../api'
import { getErrorMessage } from '../../../utils/errors';
import { showErrorMessage, showSuccessMessage } from '../../utils/messages';
import { LanguageEntityTranslationsForm } from './components/TranslationEntityForm';


export default function TranslationMunicipalitiesEdit(): React.ReactElement | null {
    const { languageCode } = useParams<{ languageCode: string }>();
    const msgs = useRef<Messages>(null!);

    const [translations, setTranslations] = useState<MunicipalityTranslationsResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [processing, setProcessing] = useState<boolean>(false);

    useEffect(() => {
        if (!languageCode) {
            setLoading(false);
            return;
        }

        const fetchTranslations = async () => {
            try {
                const data = await getApiClient().getAdminMunicipalityTranslationsByLanguageCode(languageCode);
                setTranslations(data);
            } catch (error) {
                showErrorMessage(msgs, getErrorMessage(error) ?? 'Nepodařilo se načíst překlady municipalit');
            } finally {
                setLoading(false);
            }
        };

        fetchTranslations();
    }, [languageCode]);

    const onSubmit = async (data: MunicipalityTranslationsEditRequest): Promise<void> => {
        if (!languageCode) return;

        setProcessing(true);
        try {
            await getApiClient().putAdminMunicipalityTranslationsByLanguageCode(languageCode, data);
            showSuccessMessage(msgs, 'Překlady municipalit byly úspěšně uloženy');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (error) {
            showErrorMessage(msgs, getErrorMessage(error) ?? 'Nepodařilo se uložit překlady municipalit', { replace: true });
        } finally {
            setProcessing(false);
        }
    };

    if (loading) return <ProgressSpinner />;

    return (
        <>
            <Messages ref={msgs} className='page__messages' />

            <LanguageEntityTranslationsForm
                translations={translations}
                itemsFieldName="municipalities"
                identityColumns={[
                    { field: 'municipality_id', header: 'ID municipality' },
                    { field: 'text_id', header: 'Textové ID' }
                ]}
                translationColumns={[
                    { field: 'nazev', header: 'Název', required: true },
                    { field: 'nazev_genitiv', header: 'Název (2. pád, volitelné)', required: false }
                ]}
                processing={processing}
                onSubmit={onSubmit}
                buildPayload={(formData) => ({
                    municipalities: formData.municipalities.map((item) => ({
                        municipality_id: item.municipality_id,
                        nazev: item.nazev ?? '',
                        nazev_genitiv: item.nazev_genitiv ?? null
                    }))
                })}
            />
        </>
    );
}
